import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Plus, X, Zap, Cog, Sparkles, Clock3, Play, RotateCcw, Lock } from 'lucide-react';
import { BEHAVIOR_CATEGORIES, EVENT_TRIGGERS, BEHAVIOR_METADATA, getRequirements, checkAvailability, PRESETS, VANILLA_ENTITIES } from '../../utils/behaviorsSystem';
import * as LucideIcons from 'lucide-react';

// Comportamientos que crean/ocultan su evento asociado AUTOMÁTICAMENTE — el usuario
// no los añade/quita a mano desde el diálogo de eventos, sólo activando el comportamiento.
const AUTO_EVENT_MAP = [
  { flag: 'canInteract', trigger: 'on_interact' },
  { flag: 'canExplode', trigger: 'on_ignite' },
  { flag: 'hasTimer', trigger: 'on_timer' },
];

const getIcon = (iconName, className = 'w-4 h-4') => {
  const Icon = LucideIcons[iconName] || LucideIcons.Circle;
  return <Icon className={className} />;
};

const getLangKey = (currentLang) => {
  return currentLang === 'ca' ? '_ca' : currentLang === 'en' ? '_en' : '_es';
};

// Etiquetas amigables para valores de select (por propId)
const SELECT_LABELS = {
  projectileType: {
    arrow: 'Flecha',
    snowball: 'Bola de nieve',
    egg: 'Huevo',
    trident: 'Tridente',
    small_fireball: 'Bola de fuego pequeña',
    fireball: 'Bola de fuego (Ghast)',
    dragon_fireball: 'Bola de fuego de dragón',
    shulker_bullet: 'Proyectil de Shulker',
    llama_spit: 'Escupitajo de llama',
    wither_skull: 'Calavera del Wither',
    wind_charge: 'Carga de viento',
    ender_pearl: 'Perla de ender',
  },
  swimType: {
    aquatic: '🐟 Acuático (peces)',
    amphibious: '🐸 Anfibio (agua y tierra)',
    aquatic_mammal: '🐬 Mamífero acuático (necesita respirar)',
  },
  flyType: {
    flying: '🪽 Volador (vuelo libre)',
    floating: '👻 Flotante (como un fantasma)',
    hover: '🐝 Hover (se queda flotando)',
  },
  targetFor: {
    player: 'Jugador',
    monster: 'Monstruos',
    animal: 'Animales',
    mob: 'Criaturas',
    undead: 'No-muertos',
    aquatic: 'Acuáticos',
    arthropod: 'Artrópodos',
  },
  transformInto: {
    self: '🧬 Esta criatura',
    ...Object.fromEntries(VANILLA_ENTITIES.map((e) => [e.id, e.label_es])),
  },
  family: {
    animal: 'Animal',
    monster: 'Monstruo',
    aquatic: 'Acuático',
    undead: 'No-muerto',
    arthropod: 'Artrópodo',
    mob: 'Criatura (mob)',
    custom: 'Personalizado',
  },
};

const FAMILY_EMOJI = { animal: '🐄', monster: '👹', aquatic: '🐟', undead: '🧟', arthropod: '🕷️', mob: '👾', custom: '🏷️' };

// Colores por categoría
const DEFAULT_COLORS = { bg: 'bg-gray-50', bgActive: 'bg-gray-500', text: 'text-gray-600', border: 'border-gray-200', borderActive: 'border-gray-500' };
const CATEGORY_COLORS = {
  identity: { bg: 'bg-indigo-50', bgActive: 'bg-indigo-500', text: 'text-indigo-600', border: 'border-indigo-200', borderActive: 'border-indigo-500' },
  stats: { bg: 'bg-red-50', bgActive: 'bg-red-500', text: 'text-red-600', border: 'border-red-200', borderActive: 'border-red-500' },
  movement: { bg: 'bg-blue-50', bgActive: 'bg-blue-500', text: 'text-blue-600', border: 'border-blue-200', borderActive: 'border-blue-500' },
  ai: { bg: 'bg-purple-50', bgActive: 'bg-purple-500', text: 'text-purple-600', border: 'border-purple-200', borderActive: 'border-purple-500' },
  targeting: { bg: 'bg-amber-50', bgActive: 'bg-amber-500', text: 'text-amber-600', border: 'border-amber-200', borderActive: 'border-amber-500' },
  combat: { bg: 'bg-orange-50', bgActive: 'bg-orange-500', text: 'text-orange-600', border: 'border-orange-200', borderActive: 'border-orange-500' },
  interaction: { bg: 'bg-green-50', bgActive: 'bg-green-500', text: 'text-green-600', border: 'border-green-200', borderActive: 'border-green-500' },
  automation: { bg: 'bg-cyan-50', bgActive: 'bg-cyan-500', text: 'text-cyan-600', border: 'border-cyan-200', borderActive: 'border-cyan-500' },
};

const getCategoryColorByPropId = (propId) => {
  for (const cat of BEHAVIOR_CATEGORIES) {
    if (cat.properties.find((p) => p.id === propId)) {
      return { ...(CATEGORY_COLORS[cat.id] || DEFAULT_COLORS), categoryId: cat.id };
    }
  }
  return CATEGORY_COLORS.stats;
};

/**
 * Aplica el behavior_config de la plantilla a la definición base de una propiedad.
 * Sólo sobrescribe los campos presentes en el config.
 */
const applyConfigToProperty = (property, config) => {
  const c = config?.[property.id];
  if (!c) return property;
  const merged = { ...property };
  if (typeof c.min === 'number') merged.min = c.min;
  if (typeof c.max === 'number') merged.max = c.max;
  if (typeof c.step === 'number') merged.step = c.step;
  if (c.default !== undefined) merged.default = c.default;
  merged._locked = !!c.locked;
  merged._disabled = c.enabled === false;
  return merged;
};

/**
 * Tarjeta individual de comportamiento activo
 */
const BehaviorCard = ({ propId, property, value, onChange, onRemove, activeProps }) => {
  const { currentLanguage } = useLanguage();
  const langKey = getLangKey(currentLanguage);
  const label = property[`label${langKey}`] || property.label_es;
  const colors = getCategoryColorByPropId(propId);

  // No mostrar si tiene showIf y la condición no está activa
  if (property.showIf && !activeProps[property.showIf]) {
    return null;
  }

  const renderControl = () => {
    if (property.type === 'boolean') {
      return (
        <div className="flex items-center gap-3">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={value === true}
              onChange={(e) => onChange(e.target.checked)}
              className="sr-only peer"
            />
            <div className={`w-11 h-6 rounded-full peer transition-colors ${value === true ? colors.bgActive : 'bg-gray-300'}`}>
              <div className={`h-5 w-5 rounded-full bg-white shadow transform transition-transform mt-0.5 ${value === true ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
          </label>
          <span className={`text-sm font-medium ${value === true ? colors.text : 'text-gray-500'}`}>
            {value === true ? 'Activado' : 'Desactivado'}
          </span>
        </div>
      );
    }

    if (property.type === 'select') {
      const labelMap = SELECT_LABELS[propId] || {};
      return (
        <Select value={String(value)} onValueChange={onChange}>
          <SelectTrigger className={`h-9 border-2 ${colors.border} bg-white`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {property.options.map((opt) => (
              <SelectItem key={opt} value={opt}>{labelMap[opt] || opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (property.type === 'multiselect') {
      const labelMap = SELECT_LABELS[propId] || {};
      const current = Array.isArray(value) ? value : [];
      const toggle = (opt) => {
        const next = current.includes(opt) ? current.filter((o) => o !== opt) : [...current, opt];
        onChange(next.length > 0 ? next : [property.options[0]]);
      };
      return (
        <div className="flex flex-wrap gap-1.5">
          {property.options.map((opt) => {
            const active = current.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border-2 transition-all flex items-center gap-1 ${
                  active ? `${colors.bgActive} text-white border-transparent` : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}
                data-testid={`multiselect-${propId}-${opt}`}
              >
                {FAMILY_EMOJI[opt] || ''} {labelMap[opt] || opt}
              </button>
            );
          })}
        </div>
      );
    }

    // number - slider + input numérico sincronizados
    const min = property.min ?? 0;
    const max = property.max ?? 100;
    const step = property.step || 1;
    const clamp = (v) => Math.min(max, Math.max(min, v));
    const percentage = ((value - min) / (max - min)) * 100;
    const isFloat = step < 1;
    const decimals = isFloat ? Math.max(0, -Math.floor(Math.log10(step))) : 0;
    const accent = colors.bgActive.includes('red') ? '#ef4444'
      : colors.bgActive.includes('blue') ? '#3b82f6'
      : colors.bgActive.includes('purple') ? '#a855f7'
      : colors.bgActive.includes('orange') ? '#f97316' : '#22c55e';

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-mono w-10 text-right shrink-0">{min}</span>
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            style={{
              background: `linear-gradient(to right, ${accent} 0%, ${accent} ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)`,
            }}
            data-testid={`slider-${propId}`}
          />
          <span className="text-xs text-gray-500 font-mono w-10 shrink-0">{max}</span>
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={isFloat ? parseFloat(value).toFixed(decimals) : value}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '' || raw === '-') return; // permitir edición temporal
              const n = parseFloat(raw);
              if (!isNaN(n)) onChange(clamp(n));
            }}
            onBlur={(e) => {
              const n = parseFloat(e.target.value);
              if (isNaN(n)) onChange(property.default ?? min);
              else onChange(clamp(n));
            }}
            className={`w-20 h-8 text-sm font-mono text-center border-2 rounded-md ${colors.border} focus:${colors.borderActive} focus:outline-none bg-white`}
            data-testid={`input-${propId}`}
          />
        </div>
      </div>
    );
  };

  const meta = BEHAVIOR_METADATA[propId] || {};
  const requires = getRequirements(propId);
  const requiresLabels = requires.map((rid) => {
    for (const c of BEHAVIOR_CATEGORIES) {
      const p = c.properties.find((pp) => pp.id === rid);
      if (p) return p[`label${langKey}`] || p.label_es;
    }
    return rid;
  });

  return (
    <div className={`bg-white rounded-xl border-2 ${colors.borderActive} p-4 shadow-sm hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <div className={`w-10 h-10 rounded-lg ${colors.bgActive} flex items-center justify-center text-white flex-shrink-0`}>
            {getIcon(property.icon, 'w-5 h-5')}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h4 className="text-sm font-semibold text-gray-900">{label}</h4>
              {meta.status === 'experimental' && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 text-[10px] font-medium">
                  Experimental
                </span>
              )}
              {meta.status === 'deprecated' && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200 text-[10px] font-medium">
                  Obsoleto
                </span>
              )}
            </div>
            {meta.mcId && (
              <div className="text-[10px] font-mono text-gray-400 truncate" title={meta.mcId}>
                {meta.mcId}
              </div>
            )}
            {requiresLabels.length > 0 && (
              <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-medium">
                Depende de: {requiresLabels.join(', ')}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={onRemove}
          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0"
          title="Quitar"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div>{renderControl()}</div>
    </div>
  );
};

/**
 * Panel de propiedades (grid de tarjetas)
 */
const PropertiesPanel = ({ values, onUpdate, isEvent = false, behaviorConfig = {}, idPrefix = '' }) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const { currentLanguage } = useLanguage();
  const langKey = getLangKey(currentLanguage);

  const activePropIds = Object.keys(values);
  
  // Obtener todas las propiedades activas (con showIf resuelto y config override aplicado)
  const activeProperties = [];
  BEHAVIOR_CATEGORIES.forEach((cat) => {
    cat.properties.forEach((prop) => {
      if (activePropIds.includes(prop.id)) {
        const merged = applyConfigToProperty(prop, behaviorConfig);
        activeProperties.push({ propId: prop.id, property: merged });
      }
    });
  });

  const handleAddProperty = (propId, property, withRequires = false) => {
    const merged = applyConfigToProperty(property, behaviorConfig);
    const next = { ...values };
    if (withRequires) {
      // Activar automáticamente todas las dependencias que no estén activas
      const reqs = getRequirements(propId);
      reqs.forEach((reqId) => {
        if (next[reqId] === undefined) {
          for (const cat of BEHAVIOR_CATEGORIES) {
            const reqProp = cat.properties.find((p) => p.id === reqId);
            if (reqProp) {
              const reqMerged = applyConfigToProperty(reqProp, behaviorConfig);
              next[reqId] = reqMerged.default;
              break;
            }
          }
        }
      });
    }
    next[propId] = merged.default;
    onUpdate(next);
    setShowAddDialog(false);
  };

  const handleChangeProperty = (propId, value) => {
    onUpdate({ ...values, [propId]: value });
  };

  const handleRemoveProperty = (propId) => {
    const newValues = { ...values };
    delete newValues[propId];
    // Quitar también props dependientes
    BEHAVIOR_CATEGORIES.forEach((cat) => {
      cat.properties.forEach((p) => {
        if (p.showIf === propId) {
          delete newValues[p.id];
        }
      });
    });
    onUpdate(newValues);
  };

  // Diseño nuevo: mostrar TODAS las propiedades no activas — así el usuario
  // ve el catálogo completo, aunque algunas necesiten requisitos.
  const availableCategories = BEHAVIOR_CATEGORIES.map((cat) => ({
    ...cat,
    availableProps: cat.properties.filter((p) => {
      if (activePropIds.includes(p.id)) return false;
      // Ocultar propiedades marcadas como disabled desde el Admin de la plantilla
      if (behaviorConfig[p.id]?.enabled === false) return false;
      return true;
    }),
  })).filter((cat) => cat.availableProps.length > 0);

  return (
    <div>
      {activeProperties.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-10 h-10 text-blue-500" strokeWidth={1.5} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {isEvent ? 'Sin propiedades sobrescritas' : 'Sin comportamientos'}
          </h3>
          <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
            {isEvent 
              ? 'Añade propiedades para sobrescribir el comportamiento general durante este evento.'
              : 'Añade comportamientos para dar vida a tu criatura.'
            }
          </p>
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white h-11 px-6"
            data-testid="add-first-behavior"
          >
            <Plus className="w-5 h-5 mr-2" />
            Añadir comportamiento
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
            {activeProperties.map(({ propId, property }) => (
              <BehaviorCard
                key={propId}
                propId={propId}
                property={property}
                value={values[propId]}
                onChange={(v) => handleChangeProperty(propId, v)}
                onRemove={() => handleRemoveProperty(propId)}
                activeProps={values}
              />
            ))}
          </div>

          <button
            onClick={() => setShowAddDialog(true)}
            className="w-full py-3 border-2 border-dashed border-blue-200 rounded-xl text-blue-600 hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 font-medium"
            data-testid="add-behavior-button"
          >
            <Plus className="w-5 h-5" />
            Añadir comportamiento
          </button>
        </>
      )}

      {/* Dialog añadir comportamiento — con dependencias visibles */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl">Añadir comportamiento</DialogTitle>
            <p className="text-xs text-gray-500 mt-1">
              Los comportamientos marcados con <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-medium">Requiere ⋯</span> se
              pueden añadir con un clic — activaremos las dependencias por ti.
            </p>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-6 pr-2">
            {availableCategories.length === 0 ? (
              <p className="text-center text-gray-500 py-12">
                Todos los comportamientos ya están añadidos.
              </p>
            ) : (
              availableCategories.map((cat) => {
                const colors = CATEGORY_COLORS[cat.id] || DEFAULT_COLORS;
                const catLabel = cat[`label${langKey}`] || cat.label_es;
                return (
                  <div key={cat.id}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-8 h-8 rounded-lg ${colors.bgActive} flex items-center justify-center text-white`}>
                        {getIcon(cat.icon, 'w-4 h-4')}
                      </div>
                      <h3 className="text-base font-semibold text-gray-900">{catLabel}</h3>
                      <span className="text-xs text-gray-400 font-mono">{cat.availableProps.length}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-2">
                      {cat.availableProps.map((prop) => {
                        const label = prop[`label${langKey}`] || prop.label_es;
                        const meta = BEHAVIOR_METADATA[prop.id] || {};
                        const availability = checkAvailability(prop.id, activePropIds);
                        const hasConflict = availability.conflictsWith.length > 0;
                        const missingReqs = availability.missingRequires;
                        const missingReqLabels = missingReqs.map((rid) => {
                          for (const c of BEHAVIOR_CATEGORIES) {
                            const p = c.properties.find((pp) => pp.id === rid);
                            if (p) return p[`label${langKey}`] || p.label_es;
                          }
                          return rid;
                        });
                        const conflictLabels = availability.conflictsWith.map((cid) => {
                          for (const c of BEHAVIOR_CATEGORIES) {
                            const p = c.properties.find((pp) => pp.id === cid);
                            if (p) return p[`label${langKey}`] || p.label_es;
                          }
                          return cid;
                        });
                        const canAdd = !hasConflict;
                        const isExperimental = meta.status === 'experimental';
                        const isDeprecated = meta.status === 'deprecated';
                        return (
                          <button
                            key={prop.id}
                            onClick={() => canAdd && handleAddProperty(prop.id, prop, missingReqs.length > 0)}
                            disabled={!canAdd}
                            title={meta.description_es || label}
                            className={`text-left p-2.5 rounded-lg border-2 transition-all flex items-center gap-2.5 group relative ${
                              hasConflict
                                ? 'border-red-200 bg-red-50/40 cursor-not-allowed opacity-60'
                                : missingReqs.length > 0
                                ? 'border-amber-300 bg-white hover:bg-amber-50'
                                : `border-gray-200 bg-white hover:${colors.border} hover:${colors.bg}`
                            }`}
                            data-testid={`add-prop-${prop.id}`}
                          >
                            <div className={`w-9 h-9 rounded-lg ${colors.bg} ${colors.text} flex items-center justify-center flex-shrink-0`}>
                              {getIcon(prop.icon, 'w-4 h-4')}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-medium text-gray-900 truncate">{label}</span>
                                {isExperimental && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0" title="Experimental" />
                                )}
                              </div>
                              {(missingReqs.length > 0 || hasConflict) && (
                                <div className="text-[10px] font-medium truncate mt-0.5">
                                  {hasConflict ? (
                                    <span className="text-red-600">✕ {conflictLabels.join(', ')}</span>
                                  ) : (
                                    <span className="text-amber-700">🔗 {missingReqLabels.join(', ')}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/**
 * Editor principal de comportamientos
 */
const BehaviorsEditor = ({ behaviors, onUpdate, behaviorConfig = {} }) => {
  const { currentLanguage } = useLanguage();
  const langKey = getLangKey(currentLanguage);
  const [activeTab, setActiveTab] = useState('general');
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showPresetPicker, setShowPresetPicker] = useState(false);

  const general = behaviors.general || {};
  const events = behaviors.events || [];
  const functions = behaviors.functions || [];
  const hasAnyBehavior = Object.keys(general).length > 0;

  // Eventos "dinámicos": aparecen solos cuando su comportamiento está activo,
  // y desaparecen solos si se desactiva — sin que el usuario tenga que gestionarlos.
  useEffect(() => {
    let next = [...events];
    let changed = false;
    AUTO_EVENT_MAP.forEach(({ flag, trigger }) => {
      const hasFlag = !!general[flag];
      const idx = next.findIndex((e) => e.trigger === trigger);
      if (hasFlag && idx === -1) {
        next.push({ id: `evt_${trigger}_${Date.now()}`, trigger, overrides: {} });
        changed = true;
      } else if (!hasFlag && idx !== -1) {
        next.splice(idx, 1);
        changed = true;
      }
    });
    if (changed) onUpdate({ ...behaviors, events: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [general.canInteract, general.canExplode, general.hasTimer]);

  // Aplica un preset (plantilla inicial OPCIONAL). Sobrescribe los defaults
  // pero preserva los valores que el usuario ya haya personalizado.
  const applyPreset = (presetId) => {
    const preset = PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    const merged = { ...general };
    Object.entries(preset.general).forEach(([k, v]) => {
      if (general[k] === undefined) merged[k] = v;
    });
    onUpdate({ ...behaviors, general: merged });
    setShowPresetPicker(false);
  };

  const updateGeneral = (newGeneral) => {
    onUpdate({ ...behaviors, general: newGeneral });
  };

  const addEvent = (triggerId) => {
    const newEvent = {
      id: `evt_${Date.now()}`,
      trigger: triggerId,
      overrides: {},
    };
    onUpdate({ ...behaviors, events: [...events, newEvent] });
    setShowEventDialog(false);
    setActiveTab(`event_${events.length}`);
  };

  const updateEvent = (eventIdx, patch) => {
    const newEvents = [...events];
    newEvents[eventIdx] = { ...newEvents[eventIdx], ...patch };
    onUpdate({ ...behaviors, events: newEvents });
  };

  const removeEvent = (eventIdx) => {
    const newEvents = events.filter((_, i) => i !== eventIdx);
    onUpdate({ ...behaviors, events: newEvents });
    if (activeTab === `event_${eventIdx}`) {
      setActiveTab('general');
    }
  };

  // ===== Funciones (Esperar / Acción / Repetir) =====
  const addFunction = () => {
    const newFn = { id: `fn_${Date.now()}`, name: `Función ${functions.length + 1}`, steps: [] };
    onUpdate({ ...behaviors, functions: [...functions, newFn] });
    setActiveTab(`function_${functions.length}`);
  };

  const updateFunction = (fnIdx, patch) => {
    const next = [...functions];
    next[fnIdx] = { ...next[fnIdx], ...patch };
    onUpdate({ ...behaviors, functions: next });
  };

  const removeFunction = (fnIdx) => {
    const removedId = functions[fnIdx]?.id;
    const next = functions.filter((_, i) => i !== fnIdx);
    // Cualquier evento que ejecutara esta función vuelve a modo "acción directa"
    const newEvents = events.map((e) => (e.functionId === removedId ? { ...e, functionId: undefined } : e));
    onUpdate({ ...behaviors, functions: next, events: newEvents });
    if (activeTab === `function_${fnIdx}`) setActiveTab('general');
  };

  const addStep = (fnIdx, type) => {
    const fn = functions[fnIdx];
    const steps = [...(fn.steps || [])];
    if (type === 'wait') steps.push({ type: 'wait', seconds: 2 });
    else if (type === 'action') steps.push({ type: 'action', overrides: {} });
    else if (type === 'repeat') steps.push({ type: 'repeat' });
    updateFunction(fnIdx, { steps });
  };

  const updateStep = (fnIdx, stepIdx, patch) => {
    const fn = functions[fnIdx];
    const steps = [...(fn.steps || [])];
    steps[stepIdx] = { ...steps[stepIdx], ...patch };
    updateFunction(fnIdx, { steps });
  };

  const removeStep = (fnIdx, stepIdx) => {
    const fn = functions[fnIdx];
    const steps = (fn.steps || []).filter((_, i) => i !== stepIdx);
    updateFunction(fnIdx, { steps });
  };

  const getTriggerLabel = (trigger) => {
    const t = EVENT_TRIGGERS.find((e) => e.id === trigger);
    return t ? (t[`label${langKey}`] || t.label_es) : trigger;
  };

  const getTriggerIcon = (trigger) => {
    const t = EVENT_TRIGGERS.find((e) => e.id === trigger);
    return t?.icon || 'Zap';
  };

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <div className="w-64 bg-gradient-to-b from-blue-50 to-white border-r border-blue-100 overflow-y-auto flex-shrink-0">
        <div className="p-3">
          <div className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-2 px-2">
            Comportamiento
          </div>
          <button
            onClick={() => setActiveTab('general')}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
              activeTab === 'general'
                ? 'bg-blue-600 text-white font-semibold shadow-md'
                : 'text-gray-700 hover:bg-blue-100'
            }`}
            data-testid="tab-general-behavior"
          >
            <Cog className="w-4 h-4" />
            <span>General</span>
            {Object.keys(general).length > 0 && (
              <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${activeTab === 'general' ? 'bg-white/25 text-white' : 'bg-blue-100 text-blue-700'}`}>
                {Object.keys(general).length}
              </span>
            )}
          </button>
        </div>

        <div className="p-3 border-t border-blue-100">
          <div className="flex items-center justify-between mb-2 px-2">
            <div className="text-xs font-bold text-blue-700 uppercase tracking-wider">
              Eventos
            </div>
            <button
              onClick={() => setShowEventDialog(true)}
              className="w-6 h-6 rounded-md bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-colors shadow-sm"
              title="Crear evento"
              data-testid="create-event-button"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {events.length === 0 ? (
            <p className="text-xs text-gray-500 px-2 py-2 italic">
              Sin eventos aún.
            </p>
          ) : (
            <div className="space-y-1">
              {events.map((event, idx) => {
                const isActive = activeTab === `event_${idx}`;
                const overrideCount = Object.keys(event.overrides || {}).length;
                const isAuto = AUTO_EVENT_MAP.some((a) => a.trigger === event.trigger);
                const fn = functions.find((f) => f.id === event.functionId);
                return (
                  <div
                    key={event.id}
                    className={`group flex items-center gap-1 rounded-lg transition-all ${
                      isActive ? 'bg-blue-600 shadow-md' : 'hover:bg-blue-100'
                    }`}
                    data-testid={`event-item-${event.trigger}`}
                  >
                    <button
                      onClick={() => setActiveTab(`event_${idx}`)}
                      className={`flex-1 flex items-center gap-2 px-3 py-2 text-sm min-w-0 ${
                        isActive ? 'text-white font-semibold' : 'text-gray-700'
                      }`}
                    >
                      {getIcon(getTriggerIcon(event.trigger), 'w-3.5 h-3.5 flex-shrink-0')}
                      <span className="truncate flex-1 text-left">
                        {getTriggerLabel(event.trigger)}
                      </span>
                      {fn && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 flex items-center gap-1 ${isActive ? 'bg-white/25 text-white' : 'bg-purple-100 text-purple-700'}`}>
                          <Play className="w-2.5 h-2.5" />{fn.name}
                        </span>
                      )}
                      {!fn && overrideCount > 0 && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${isActive ? 'bg-white/25 text-white' : 'bg-blue-100 text-blue-700'}`}>
                          {overrideCount}
                        </span>
                      )}
                    </button>
                    {isAuto ? (
                      <span className={`p-1.5 mr-1 ${isActive ? 'text-white/60' : 'text-gray-300'}`} title="Se gestiona automáticamente según el comportamiento activo">
                        <Lock className="w-3.5 h-3.5" />
                      </span>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeEvent(idx);
                        }}
                        className={`p-1.5 opacity-0 group-hover:opacity-100 rounded mr-1 ${isActive ? 'text-white hover:bg-white/20' : 'text-gray-400 hover:text-red-500'}`}
                        title="Eliminar"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-3 border-t border-blue-100">
          <div className="flex items-center justify-between mb-2 px-2">
            <div className="text-xs font-bold text-blue-700 uppercase tracking-wider">
              Funciones
            </div>
            <button
              onClick={addFunction}
              className="w-6 h-6 rounded-md bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center transition-colors shadow-sm"
              title="Crear función"
              data-testid="create-function-button"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          {functions.length === 0 ? (
            <p className="text-xs text-gray-500 px-2 py-2 italic">
              Secuencias reutilizables (esperar, actuar, repetir).
            </p>
          ) : (
            <div className="space-y-1">
              {functions.map((fn, idx) => {
                const isActive = activeTab === `function_${idx}`;
                return (
                  <div
                    key={fn.id}
                    className={`group flex items-center gap-1 rounded-lg transition-all ${
                      isActive ? 'bg-purple-600 shadow-md' : 'hover:bg-purple-100'
                    }`}
                  >
                    <button
                      onClick={() => setActiveTab(`function_${idx}`)}
                      className={`flex-1 flex items-center gap-2 px-3 py-2 text-sm min-w-0 ${
                        isActive ? 'text-white font-semibold' : 'text-gray-700'
                      }`}
                      data-testid={`function-tab-${fn.id}`}
                    >
                      <Play className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate flex-1 text-left">{fn.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${isActive ? 'bg-white/25 text-white' : 'bg-purple-100 text-purple-700'}`}>
                        {(fn.steps || []).length}
                      </span>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeFunction(idx); }}
                      className={`p-1.5 opacity-0 group-hover:opacity-100 rounded mr-1 ${isActive ? 'text-white hover:bg-white/20' : 'text-gray-400 hover:text-red-500'}`}
                      title="Eliminar función"
                      data-testid={`remove-function-${fn.id}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="max-w-5xl mx-auto p-6">
          {activeTab === 'general' && (
            <>
              <div className="mb-6 flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-blue-600 flex items-center justify-center text-white shadow-lg">
                  <Cog className="w-7 h-7" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Comportamiento General</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Cómo se comporta tu criatura normalmente
                  </p>
                </div>
              </div>

              {/* Presets — plantilla inicial OPCIONAL, nunca bloquea la exportación */}
              {(!hasAnyBehavior || showPresetPicker) ? (
                <div className="mb-6 p-4 rounded-xl border-2 border-blue-200 bg-blue-50" data-testid="preset-picker">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-semibold text-blue-900">¿Quieres empezar con un preset?</h3>
                    {hasAnyBehavior && (
                      <button onClick={() => setShowPresetPicker(false)} className="text-blue-400 hover:text-blue-700" data-testid="dismiss-preset-picker">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-blue-800 mb-3">Es opcional. Aplica varios comportamientos de golpe — luego puedes cambiarlos como quieras.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {PRESETS.map((preset) => (
                      <button
                        key={preset.id}
                        onClick={() => applyPreset(preset.id)}
                        title={preset.description_es}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-blue-200 bg-white hover:border-blue-500 hover:bg-blue-100 text-left transition-all"
                        data-testid={`pick-preset-${preset.id}`}
                      >
                        <span className="text-lg flex-shrink-0">{preset.emoji}</span>
                        <span className="text-xs font-medium text-gray-800 truncate">{preset[`label${langKey}`] || preset.label_es}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowPresetPicker(true)}
                  className="mb-6 text-xs font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1.5"
                  data-testid="open-preset-picker"
                >
                  <Sparkles className="w-3.5 h-3.5" /> Aplicar un preset
                </button>
              )}

              <PropertiesPanel values={general} onUpdate={updateGeneral} isEvent={false} behaviorConfig={behaviorConfig} />
            </>
          )}

          {activeTab.startsWith('event_') && (() => {
            const idx = parseInt(activeTab.replace('event_', ''), 10);
            const event = events[idx];
            if (!event) return null;
            const mode = event.functionId ? 'function' : 'simple';
            return (
              <>
                <div className="mb-6 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-blue-600 flex items-center justify-center text-white shadow-lg">
                    {getIcon(getTriggerIcon(event.trigger), 'w-7 h-7')}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {getTriggerLabel(event.trigger)}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      ¿Qué debe pasar cuando ocurra este evento?
                    </p>
                  </div>
                </div>

                <div className="flex gap-2 mb-6">
                  <button
                    onClick={() => updateEvent(idx, { functionId: undefined })}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium border-2 transition-all ${
                      mode === 'simple' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                    }`}
                    data-testid={`event-mode-simple-${idx}`}
                  >
                    <Cog className="w-4 h-4" /> Cambiar valores
                  </button>
                  <button
                    onClick={() => updateEvent(idx, { functionId: event.functionId || functions[0]?.id })}
                    disabled={functions.length === 0}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium border-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                      mode === 'function' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300'
                    }`}
                    title={functions.length === 0 ? 'Crea primero una función en el apartado "Funciones"' : ''}
                    data-testid={`event-mode-function-${idx}`}
                  >
                    <Play className="w-4 h-4" /> Ejecutar función
                  </button>
                </div>

                {mode === 'function' ? (
                  <div className="space-y-3">
                    <Label className="text-xs text-gray-600">Función a ejecutar</Label>
                    <Select value={event.functionId} onValueChange={(v) => updateEvent(idx, { functionId: v })}>
                      <SelectTrigger className="h-10 bg-white border-2 border-purple-200" data-testid={`event-function-select-${idx}`}>
                        <SelectValue placeholder="Elige una función" />
                      </SelectTrigger>
                      <SelectContent>
                        {functions.map((fn) => (
                          <SelectItem key={fn.id} value={fn.id}>{fn.name} ({(fn.steps || []).length} pasos)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <PropertiesPanel
                    values={event.overrides || {}}
                    onUpdate={(newOverrides) => updateEvent(idx, { overrides: newOverrides })}
                    isEvent={true}
                    behaviorConfig={behaviorConfig}
                  />
                )}
              </>
            );
          })()}

          {activeTab.startsWith('function_') && (() => {
            const idx = parseInt(activeTab.replace('function_', ''), 10);
            const fn = functions[idx];
            if (!fn) return null;
            return (
              <>
                <div className="mb-6 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-purple-600 flex items-center justify-center text-white shadow-lg">
                    <Play className="w-7 h-7" />
                  </div>
                  <div className="flex-1">
                    <Input
                      value={fn.name}
                      onChange={(e) => updateFunction(idx, { name: e.target.value })}
                      className="text-xl font-bold h-10 border-0 border-b-2 rounded-none px-0 focus-visible:ring-0 border-gray-200 focus:border-purple-500"
                      data-testid={`function-name-input-${idx}`}
                    />
                    <p className="text-sm text-gray-600 mt-1">
                      Secuencia reutilizable: Esperar, Acción o Repetir. Úsala desde varios eventos.
                    </p>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  {(fn.steps || []).length === 0 && (
                    <p className="text-sm text-gray-500 italic py-6 text-center">Todavía sin pasos. Añade uno abajo.</p>
                  )}
                  {(fn.steps || []).map((step, sIdx) => (
                    <div key={sIdx} className="bg-white rounded-xl border-2 border-purple-100 p-3" data-testid={`function-step-${idx}-${sIdx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-sm font-semibold text-purple-700">
                          {step.type === 'wait' && <><Clock3 className="w-4 h-4" /> Esperar</>}
                          {step.type === 'action' && <><Cog className="w-4 h-4" /> Acción</>}
                          {step.type === 'repeat' && <><RotateCcw className="w-4 h-4" /> Repetir (vuelve al inicio)</>}
                        </div>
                        <button onClick={() => removeStep(idx, sIdx)} className="p-1 text-gray-400 hover:text-red-500" data-testid={`remove-step-${idx}-${sIdx}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {step.type === 'wait' && (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={0.05}
                            max={3600}
                            step={0.5}
                            value={step.seconds}
                            onChange={(e) => updateStep(idx, sIdx, { seconds: parseFloat(e.target.value) || 1 })}
                            className="w-24 h-9 text-sm text-center border-2 rounded-md border-purple-200 bg-white"
                            data-testid={`step-wait-seconds-${idx}-${sIdx}`}
                          />
                          <span className="text-sm text-gray-500">segundos</span>
                        </div>
                      )}
                      {step.type === 'action' && (
                        <PropertiesPanel
                          values={step.overrides || {}}
                          onUpdate={(newOverrides) => updateStep(idx, sIdx, { overrides: newOverrides })}
                          isEvent={true}
                          behaviorConfig={behaviorConfig}
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2">
                  <button onClick={() => addStep(idx, 'wait')} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 border-dashed border-purple-200 text-purple-700 hover:bg-purple-50 text-sm font-medium" data-testid={`add-step-wait-${idx}`}>
                    <Clock3 className="w-4 h-4" /> Esperar
                  </button>
                  <button onClick={() => addStep(idx, 'action')} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 border-dashed border-purple-200 text-purple-700 hover:bg-purple-50 text-sm font-medium" data-testid={`add-step-action-${idx}`}>
                    <Cog className="w-4 h-4" /> Acción
                  </button>
                  <button onClick={() => addStep(idx, 'repeat')} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 border-dashed border-purple-200 text-purple-700 hover:bg-purple-50 text-sm font-medium" data-testid={`add-step-repeat-${idx}`}>
                    <RotateCcw className="w-4 h-4" /> Repetir
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* Event trigger dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>¿Cuándo ocurre el evento?</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-1 max-h-96 overflow-y-auto">
            {EVENT_TRIGGERS.filter((t) => !AUTO_EVENT_MAP.some((a) => a.trigger === t.id)).map((trigger) => {
              // Uniqueness: si el trigger es uniquePerEntity=true y ya está en uso,
              // deshabilitar el botón (Bedrock no permite dos eventos con el mismo nombre).
              const alreadyUsed = (events || []).some((e) => e.trigger === trigger.id);
              const missingGeneral = trigger.requiresGeneral && !general[trigger.requiresGeneral];
              const isDisabled = (trigger.uniquePerEntity !== false && alreadyUsed) || missingGeneral;
              return (
                <button
                  key={trigger.id}
                  onClick={() => !isDisabled && addEvent(trigger.id)}
                  disabled={isDisabled}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors border ${
                    isDisabled
                      ? 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                      : 'border-transparent hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200'
                  }`}
                  data-testid={`event-trigger-${trigger.id}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                    isDisabled ? 'bg-gray-100 text-gray-400' : 'bg-blue-100 text-blue-600'
                  }`}>
                    {getIcon(trigger.icon, 'w-4 h-4')}
                  </div>
                  <span className="text-sm font-medium flex-1">
                    {trigger[`label${langKey}`] || trigger.label_es}
                  </span>
                  {missingGeneral && (
                    <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                      Requiere activar
                    </span>
                  )}
                  {!missingGeneral && alreadyUsed && trigger.uniquePerEntity !== false && (
                    <span className="text-[10px] font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5">
                      Ya en uso
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BehaviorsEditor;
